import React, { useRef, useState } from 'react';
import { Paperclip, X, FileText, Image as ImageIcon } from 'lucide-react';
import AnteprimaDocumento from '../AnteprimaDocumento';

// Allegati del supporto (21/10): foto JPG/PNG e PDF, al massimo 3 per messaggio e 5 MB l'uno.
// Le foto si riducono nel browser (lato lungo 1600 px, JPG) prima di partire: pesano poco e perdono i dati GPS.
export const MAX_ALLEGATI = 3;
const MAX_MB = 5;

const riduciFoto = (file) => new Promise((ok, ko) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    const g = c.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height); g.drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    ok(c.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = () => { URL.revokeObjectURL(url); ko(new Error('foto non leggibile')); };
  img.src = url;
});
const leggi = (file) => new Promise((ok, ko) => { const r = new FileReader(); r.onload = () => ok(r.result); r.onerror = () => ko(new Error('file non leggibile')); r.readAsDataURL(file); });

// files = [{ nome, dati (data URL), tipo: 'foto'|'pdf' }]
export function SceltaAllegati({ files, setFiles, onErrore }) {
  const input = useRef(null);
  const [lavoro, setLavoro] = useState(false);
  const aggiungi = async (lista) => {
    onErrore?.('');
    const nuovi = [];
    setLavoro(true);
    for (const f of lista) {
      if (files.length + nuovi.length >= MAX_ALLEGATI) { onErrore?.(`Al massimo ${MAX_ALLEGATI} allegati per messaggio`); break; }
      try {
        if (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)) {
          if (f.size > MAX_MB * 1024 * 1024) { onErrore?.(`"${f.name}" è troppo grande (massimo ${MAX_MB} MB)`); continue; }
          nuovi.push({ nome: f.name, dati: await leggi(f), tipo: 'pdf' });
        } else if (f.type.startsWith('image/')) {
          nuovi.push({ nome: f.name.replace(/\.[^.]+$/, '') + '.jpg', dati: await riduciFoto(f), tipo: 'foto' });
        } else onErrore?.(`"${f.name}": puoi allegare solo foto o PDF`);
      } catch { onErrore?.(`"${f.name}" non si riesce a leggere (prova a fare uno screenshot)`); }
    }
    setLavoro(false);
    setFiles([...files, ...nuovi]);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
      <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/heic,image/webp,application/pdf" style={{ display: 'none' }}
        onChange={e => { aggiungi([...e.target.files]); e.target.value = ''; }} />
      {files.length < MAX_ALLEGATI && (
        <button type="button" style={st.allega} onClick={() => input.current?.click()} disabled={lavoro} title={`Foto o PDF, massimo ${MAX_ALLEGATI}, ${MAX_MB} MB l'uno`}>
          <Paperclip size={14} /> {lavoro ? 'Preparo…' : 'Allega foto o PDF'}
        </button>
      )}
      {files.map((f, i) => (
        <span key={i} style={st.chip}>
          {f.tipo === 'pdf' ? <FileText size={13} /> : <ImageIcon size={13} />}
          <span style={st.chipNome}>{f.nome}</span>
          <button type="button" style={st.togli} onClick={() => setFiles(files.filter((_, j) => j !== i))} title="Togli"><X size={13} /></button>
        </span>
      ))}
    </div>
  );
}

// Allegati di un messaggio: un tocco apre l'anteprima (scaricati dall'API con gli stessi permessi della richiesta)
export function ElencoAllegati({ allegati, chiaro }) {
  const [aperto, setAperto] = useState(null);
  if (!allegati?.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
      {allegati.map(a => (
        <button key={a.id} type="button" onClick={() => setAperto(a)} style={{ ...st.chip, cursor: 'pointer', ...(chiaro ? st.chipChiaro : {}) }}>
          {a.tipo === 'pdf' ? <FileText size={13} /> : <ImageIcon size={13} />}<span style={st.chipNome}>{a.nome}</span>
        </button>
      ))}
      {aperto && <AnteprimaDocumento doc={{ nome: aperto.nome, file_nome: `${aperto.nome.replace(/\.[^.]+$/, '')}.${aperto.tipo}` }} endpoint={`/tickets/allegati/${aperto.id}`} onClose={() => setAperto(null)} />}
    </div>
  );
}

const st = {
  allega: { display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px dashed var(--border2)', background: 'transparent', color: 'var(--text-muted)', borderRadius: '999px', padding: '6px 12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '5px', border: '1px solid var(--border2)', background: 'var(--bg-input)', color: 'var(--text)', borderRadius: '999px', padding: '4px 10px', fontSize: '12.5px', maxWidth: '220px' },
  chipChiaro: { background: 'rgba(255,255,255,.18)', borderColor: 'rgba(255,255,255,.35)', color: '#fff' },
  chipNome: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  togli: { border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-dim)', display: 'flex' },
};
